import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, Menu, X, Plus, ChevronDown } from "lucide-react";
import { categorySlug } from "@/lib/api";
import { MarketTicker } from "@/components/MarketTicker";
import NewsTicker from "@/components/NewsTicker";
import HeaderWeather from "@/components/HeaderWeather";
import { Poppy } from "@/components/Poppy";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// Main nav — explicit order from the brief
const MAIN_NAV = [
  { label: "News",          to: "/news" },
  { label: "Sport",         to: "/sport" },
  { label: "Markets",       to: "/markets" },
  { label: "Lifestyle",     to: "/category/lifestyle" },
  { label: "Culture",       to: "/category/culture" },
  { label: "Health",        to: "/category/health" },
  { label: "Fashion",       to: "/category/fashion" },
  { label: "Food & Drink",  to: "/category/food-drink" },
  { label: "Technology",    to: "/category/technology" },
  { label: "Art & Design",  to: "/category/art-design" },
];

// Plus-dropdown — alphabetical
const PLUS_NAV = [
  { label: "Hinton Bar",            to: "/hinton-bar" },
  { label: "Hinton Shops",          to: "/hinton-shops" },
  { label: "Hinton Visits",         to: "/category/hinton-visits" },
  { label: "In Conversation With…", to: "/in-conversation-with" },
];

// Default to the user's browser timezone; fall back to UK if undetectable.
function userTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || "Europe/London";
  } catch {
    return "Europe/London";
  }
}

function HeaderClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const tz = userTimezone();
  const datePart = new Intl.DateTimeFormat("en-GB", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric", timeZone: tz,
  }).format(now);
  const timePart = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz,
  }).format(now);
  // Compact timezone label (e.g. "GMT", "BST", "EST")
  const tzLabel = new Intl.DateTimeFormat("en-GB", { timeZone: tz, timeZoneName: "short" })
    .formatToParts(now).find((p) => p.type === "timeZoneName")?.value || "";
  return (
    <span className="inline-flex items-center gap-3">
      <span>{datePart}</span>
      <span className="text-[var(--hinton-red)]">·</span>
      <span data-testid="header-clock-time">{timePart}{tzLabel ? ` ${tzLabel}` : ""}</span>
    </span>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock the page body when the mobile menu is open so iOS Safari doesn't
  // bubble the page underneath up and "steal" the menu scroll. iOS ignores
  // `body.style.overflow = hidden` in many cases, so we use the
  // position:fixed trick (the only technique that actually works on iOS).
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      document.body.style.overflow = prev.overflow;
      // Restore previous scroll position.
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!searchValue.trim()) return;
    setSearchOpen(false);
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b transition-colors ${scrolled ? "border-black/10" : "border-transparent"}`}
      data-testid="site-header"
    >
      {/* Top strip — live date & time (user's local TZ, falls back to UK) */}
      <div className="hidden md:flex items-center justify-between px-6 lg:px-10 py-2 text-[11px] tracking-[0.2em] uppercase text-neutral-500 border-b border-neutral-200/70 font-mono-ed">
        <span data-testid="header-date">
          <HeaderClock />
        </span>
        <HeaderWeather />
      </div>

      {/* Main row — logo + nav + actions on a single line */}
      <div className="flex items-center gap-5 lg:gap-7 px-4 sm:px-6 lg:px-10 py-3 md:py-4">
        <button
          aria-label="Open menu"
          className="xl:hidden p-2 -ml-2 md:p-3"
          onClick={() => setOpen((s) => !s)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>

        <Link to="/" className="wordmark text-[28px] sm:text-[30px] md:text-[32px] lg:text-[36px] tracking-[-0.04em] leading-none flex-shrink-0 inline-flex items-center gap-1.5 md:gap-2" data-testid="brand-logo">
          <span>HINTON<span className="text-[var(--hinton-red)]">.</span></span>
          <Poppy className="w-[24px] h-[24px] md:w-[28px] md:h-[28px] lg:w-[32px] lg:h-[32px] -translate-y-[2px]" />
        </Link>

        {/* Inline nav */}
        <nav className="hidden xl:flex flex-1 items-center justify-center gap-5 text-[12px] font-semibold tracking-[0.14em] uppercase">
          {MAIN_NAV.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className={({ isActive }) => `link-expand whitespace-nowrap ${isActive ? "text-[var(--hinton-red)]" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              {item.label}
            </NavLink>
          ))}

          {/* + dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1 text-[var(--hinton-red)] hover:text-black transition-colors focus:outline-none"
                aria-label="More sections"
                data-testid="nav-plus-trigger"
              >
                <Plus size={16} strokeWidth={2.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={12}
              className="rounded-none border-black/15 bg-white min-w-[240px] p-0 shadow-xl"
              data-testid="nav-plus-menu"
            >
              {PLUS_NAV.map((item) => (
                <DropdownMenuItem key={item.label} asChild className="rounded-none focus:bg-black focus:text-white px-5 py-3 cursor-pointer">
                  <Link
                    to={item.to}
                    className="text-[12px] uppercase tracking-[0.18em] font-semibold w-full"
                    data-testid={`nav-plus-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  >
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-1 ml-auto xl:ml-0 flex-shrink-0">
          <button
            aria-label="Search"
            className="p-2 md:p-3 hover:text-[var(--hinton-red)] transition-colors"
            onClick={() => setSearchOpen((s) => !s)}
            data-testid="header-search-toggle"
          >
            <Search size={20} />
          </button>
        </div>
      </div>

      {/* Admin-toggled marquee under the nav: live markets (default), or
          a custom Breaking / Latest News strap with an admin-set headline. */}
      <NewsTicker />

      {/* Search drop */}
      {searchOpen && (
        <div className="border-t border-neutral-200 bg-white px-4 sm:px-8 py-6">
          <form onSubmit={submitSearch} className="max-w-3xl mx-auto flex gap-3 items-center" data-testid="header-search-form">
            <input
              autoFocus
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search Hinton. Articles, interviews, places…"
              className="flex-1 bg-transparent outline-none border-b border-black/40 focus:border-[var(--hinton-red)] py-2 text-lg font-serif-ed italic placeholder:text-neutral-400"
              data-testid="header-search-input"
            />
            <button type="submit" className="px-5 py-2 bg-black text-white text-xs uppercase tracking-widest font-semibold hover:bg-[var(--hinton-red)] transition-colors" data-testid="header-search-submit">
              Search
            </button>
          </form>
        </div>
      )}

      {/* Mobile nav — FIXED full-screen overlay (NOT nested inside sticky
          header) so iOS Safari treats it as its own scroll container. Uses
          dvh for proper sizing when the URL bar shrinks the viewport. */}
      {open && (
        <div
          className="xl:hidden fixed inset-0 z-[60] bg-white flex flex-col"
          style={{ height: "100dvh", WebkitOverflowScrolling: "touch" }}
          data-testid="mobile-nav-overlay"
        >
          {/* Top bar replicating the header logo + close button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 flex-shrink-0">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="wordmark text-[26px] tracking-[-0.04em] leading-none inline-flex items-center gap-1.5"
            >
              <span>HINTON<span className="text-[var(--hinton-red)]">.</span></span>
              <Poppy size={22} />
            </Link>
            <button
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="p-2 -mr-2"
              data-testid="mobile-menu-close"
            >
              <X size={26} />
            </button>
          </div>

          {/* Scrollable content area — this is the ONLY scroll-y region */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+24px)]"
            data-testid="mobile-nav-scroll"
            style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
          >
            <div className="md:grid md:grid-cols-2 md:gap-x-10 md:px-10 md:pt-6">
              {MAIN_NAV.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="block px-6 md:px-0 py-3 md:py-4 border-b border-neutral-100 text-sm md:text-base uppercase tracking-wider"
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="px-6 md:px-10 py-3 mt-2 md:mt-6 overline text-[var(--hinton-red)]">More from Hinton</div>
            <div className="md:grid md:grid-cols-2 md:gap-x-10 md:px-10">
              {PLUS_NAV.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="block px-6 md:px-0 py-3 md:py-4 border-b border-neutral-100 text-sm md:text-base uppercase tracking-wider"
                  data-testid={`mobile-nav-plus-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const year = new Date().getFullYear();
  const SOCIALS = [
    { label: "Instagram", href: "https://www.instagram.com/hintononline/", icon: "instagram" },
    { label: "Facebook",  href: "https://www.facebook.com/HintonOnline",   icon: "facebook" },
    { label: "X",         href: "https://x.com/Hinton_Online",             icon: "x" },
  ];
  return (
    <footer className="mt-20 border-t border-neutral-200 bg-[#111] text-[#F9F9F7]" data-testid="site-footer">
      <div className="px-6 lg:px-12 pt-16 pb-12 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-12 gap-10 md:gap-x-10 md:gap-y-12 xl:gap-14">
        {/* Wordmark + tagline + socials */}
        <div className="md:col-span-3 xl:col-span-5">
          <div className="wordmark text-[40px] leading-none">HINTON<span className="text-[var(--hinton-red)]">.</span></div>
          <p className="mt-5 font-serif-ed italic text-base text-neutral-300 max-w-sm leading-relaxed">
            Serious reporting, sharp cultural commentary, and the stories shaping
            Britain and the wider world.
          </p>
          {/* Socials */}
          <div className="mt-7 flex items-center gap-3" data-testid="footer-socials">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Hinton on ${s.label}`}
                className="w-10 h-10 inline-flex items-center justify-center border border-white/20 hover:border-[var(--hinton-red)] hover:bg-[var(--hinton-red)] transition-colors"
                data-testid={`footer-social-${s.icon}`}
              >
                <SocialIcon name={s.icon} />
              </a>
            ))}
          </div>
        </div>

        {/* Sections column */}
        <div className="xl:col-span-3">
          <div className="overline text-neutral-400 mb-5">Sections</div>
          <ul className="space-y-2.5 md:space-y-3.5 text-sm md:text-[15px]">
            {MAIN_NAV.map((item) => (
              <li key={item.label}>
                <Link to={item.to} className="text-neutral-200 hover:text-white transition-colors">{item.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Hinton column */}
        <div className="xl:col-span-2">
          <div className="overline text-neutral-400 mb-5">Hinton</div>
          <ul className="space-y-2.5 md:space-y-3.5 text-sm md:text-[15px]">
            {PLUS_NAV.map((item) => (
              <li key={item.label}>
                <Link to={item.to} className="text-neutral-200 hover:text-white transition-colors">{item.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Company column */}
        <div className="xl:col-span-2">
          <div className="overline text-neutral-400 mb-5">Company</div>
          <ul className="space-y-2.5 md:space-y-3.5 text-sm md:text-[15px]">
            <li><Link to="/about" className="text-neutral-200 hover:text-white transition-colors" data-testid="footer-about">About Hinton</Link></li>
            <li><Link to="/archives" className="text-neutral-200 hover:text-white transition-colors" data-testid="footer-archives">Archives</Link></li>
            <li><Link to="/working-with-hinton" className="text-neutral-200 hover:text-white transition-colors" data-testid="footer-working-with">Working with Hinton</Link></li>
            <li><Link to="/write-for-us" className="text-neutral-200 hover:text-white transition-colors" data-testid="footer-write-for-us">Write for Hinton</Link></li>
            <li><Link to="/markets" className="text-neutral-200 hover:text-white transition-colors" data-testid="footer-markets">Markets</Link></li>
            <li><Link to="/login" className="text-neutral-200 hover:text-white transition-colors" data-testid="footer-newsroom-login">Newsroom Login</Link></li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 px-6 lg:px-12 py-6 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 text-[11px] uppercase tracking-[0.22em] text-neutral-400 font-mono-ed">
        <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-2">
          <span data-testid="footer-copyright">© {year} Hinton. All rights reserved.</span>
          <span className="hidden md:inline text-neutral-700">·</span>
          <span>Part of Hinton Enterprises LTD</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Link to="/privacy-policy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
          <Link to="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies">Cookies</Link>
          <button
            type="button"
            onClick={() => { if (typeof window.openCookieSettings === "function") window.openCookieSettings(); }}
            className="hover:text-white transition-colors uppercase tracking-[0.22em]"
            data-testid="footer-cookie-settings"
          >Cookie settings</button>
        </div>
      </div>
    </footer>
  );
}

function SocialIcon({ name }) {
  // Inline SVGs (currentColor) so they pick up our hover background-flip
  // without pulling in a heavier icon dependency.
  const common = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": true };
  if (name === "instagram") {
    return (
      <svg {...common}>
        <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.43-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 1.62c-3.15 0-3.5.01-4.74.07-1.08.05-1.66.23-2.05.38-.51.2-.88.44-1.27.83-.39.39-.63.76-.83 1.27-.15.39-.33.97-.38 2.05-.06 1.24-.07 1.59-.07 4.74s.01 3.5.07 4.74c.05 1.08.23 1.66.38 2.05.2.51.44.88.83 1.27.39.39.76.63 1.27.83.39.15.97.33 2.05.38 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c1.08-.05 1.66-.23 2.05-.38.51-.2.88-.44 1.27-.83.39-.39.63-.76.83-1.27.15-.39.33-.97.38-2.05.06-1.24.07-1.59.07-4.74s-.01-3.5-.07-4.74c-.05-1.08-.23-1.66-.38-2.05-.2-.51-.44-.88-.83-1.27-.39-.39-.76-.63-1.27-.83-.39-.15-.97-.33-2.05-.38-1.24-.06-1.59-.07-4.74-.07zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 1.62a3.38 3.38 0 1 0 0 6.76 3.38 3.38 0 0 0 0-6.76zM17.84 6.78a1.16 1.16 0 1 1-2.32 0 1.16 1.16 0 0 1 2.32 0z"/>
      </svg>
    );
  }
  if (name === "facebook") {
    return (
      <svg {...common}>
        <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.99 3.66 9.13 8.44 9.88V14.9H7.9V12h2.54V9.8c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.9h-2.33v6.98C18.34 21.13 22 16.99 22 12z"/>
      </svg>
    );
  }
  if (name === "x") {
    return (
      <svg {...common}>
        <path d="M18.244 2H21l-6.52 7.45L22 22h-6.828l-4.78-6.246L4.85 22H2.094l6.97-7.97L2 2h6.914l4.328 5.713L18.244 2zm-1.196 18h1.654L7.04 4H5.27l11.778 16z"/>
      </svg>
    );
  }
  return null;
}

export default function Layout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
